import { Component, EventEmitter, Input, OnInit } from '@angular/core';

import { ChangeContext, LabelType, PointerType } from '@angular-slider/ngx-slider';

import { translate } from '@jsverse/transloco';
import { DropdownObject, TabObject } from '@inmoment/the-kitchen';

import { Project, SentimentPolarityRange } from 'app/providers/storage-api/project.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { SentimentUtils } from 'app/utils/sentiment.utils';
import { Utils } from '../../../../utils/utils';

// options for sentiment range sliders
const RANGE_DECIMAL = 1; // round values to decimals
const RANGE_STEP = 1 / Math.pow(10, RANGE_DECIMAL); // slider steps

interface ScaleType {
  id: string;
  label: string;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-project-sentiment-range',
  templateUrl: './project-sentiment-range.component.html',
  styleUrls: ['./project-sentiment-range.component.scss'],
  standalone: false,
})
export class ProjectSentimentRangeComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() project: Project;
  @Input() readOnly = false;
  @Input() embeddedButtons = true;

  public projectSentimentScaleDropdown: DropdownObject[] = [
    { title: translate('project-sentiment-range.three-point-scale'), id: '3' },
    { title: translate('project-sentiment-range.five-point-scale'), id: '5' },
  ];
  sentimentScale = 3;
  sentimentRanges = {
    document: { 3: [], 5: [] },
    feature: { 3: [], 5: [] },
  };
  defaultSentimentRanges = {
    document: {},
    feature: {},
  };

  slideRefresh = new EventEmitter<void>();
  translateFns = {
    3: [],
    5: [],
  };
  sliderOptions = {
    document: {
      3: [],
      5: [],
    },
    feature: {
      3: [],
      5: [],
    },
  };

  itemsForDisplay = {
    document: { 3: [], 5: [] },
    feature: { 3: [], 5: [] },
  };

  hasChanges: boolean;
  public sentimentTabs: TabObject[] = [
    { title: translate('project-sentiment-range.three-point-scale'), id: '3' },
    { title: translate('project-sentiment-range.five-point-scale'), id: '5' },
  ];

  readonly scales = [3, 5];
  readonly scaleTypes: ScaleType[] = [
    { id: 'document', label: translate('project-sentiment-range.document') },
    { id: 'feature', label: translate('project-sentiment-range.feature') },
  ];
  readonly polarities = {
    'very negative': translate('project-sentiment-range.polarities.very-negative'),
    'somewhat negative': translate('project-sentiment-range.polarities.somewhat-negative'),
    negative: translate('project-sentiment-range.polarities.negative'),
    neutral: translate('project-sentiment-range.polarities.neutral'),
    positive: translate('project-sentiment-range.polarities.positive'),
    'somewhat positive': translate('project-sentiment-range.polarities.somewhat-positive'),
    'very positive': translate('project-sentiment-range.polarities.very-positive'),
  };

  get step() {
    return RANGE_STEP;
  }

  get minLimit() {
    return -Utils.floor(0.99999, RANGE_DECIMAL);
  }

  get maxLimit() {
    return Utils.floor(0.99999, RANGE_DECIMAL);
  }

  ngOnInit() {
    this.scaleTypes.forEach((type) => {
      this.scales.forEach((scale) => {
        this.sentimentRanges[type.id][scale] = this.project.polarities[type.id][scale].map(
          (o: SentimentPolarityRange) => ({
            ...o,
          })
        );

        this.itemsForDisplay[type.id][scale] = this.sentimentRanges[type.id][scale].map((r) => ({
          ...r,
          sentimentScore: Utils.roundHalfAway(
            ((r.max === null ? 1.0 : r.max) + (r.min === null ? -1.0 : r.min)) / 2,
            2
          ),
        }));
      });
    });

    this.defaultSentimentRanges = SentimentUtils.getDefaultSentimentRanges();

    Object.keys(this.translateFns).forEach((k) => {
      const length = Number.parseInt(k, 10);
      Array.from(Array(length).keys()).forEach((i) =>
        this.translateFns[k].push(this.translate(length, i))
      );
    });
  }

  /**
   * Adjust sibling sliders due to a value change. Here we set max and min values for neighboring
   * sliders to match with the current one's min and max values respectively.
   *
   * @param $event ChangeContext of ngx-slider
   * @param type 'document' or 'feature'
   * @param index index of the slider on the sentiment scale (0-2 or 0-4)
   */
  adjustSiblingSlider($event: ChangeContext, type: string, index: number) {
    let adjustPrev = $event.pointerType === PointerType.Min && index !== 0;
    let adjustNext = $event.pointerType === PointerType.Max || index === 0;

    let newPrevValue = $event.value;
    let newNextValue = $event.highValue === undefined ? $event.value : $event.highValue;

    const currentSlider = this.sentimentRanges[type][this.sentimentScale][index];
    const nextSlider = this.sentimentRanges[type][this.sentimentScale][index + 1];
    const prevSlider = this.sentimentRanges[type][this.sentimentScale][index - 1];

    // In case the range limits collide
    if (currentSlider.min === currentSlider.max) {
      // Reset range max value if range max reached min + RANGE_STEP
      if (currentSlider.max === this.calcMaxLimit(type, this.sentimentScale, index)) {
        const minValue = Utils.round(currentSlider.min - RANGE_STEP, RANGE_DECIMAL);
        newPrevValue = minValue;
        setTimeout(() => (currentSlider.min = minValue));
        // Reset range min value if range min reached max - RANGE_STEP
      } else if (currentSlider.min === this.calcMinLimit(index)) {
        const maxValue = Utils.round(currentSlider.max + RANGE_STEP, RANGE_DECIMAL);
        newNextValue = maxValue;
        setTimeout(() => (currentSlider.max = maxValue));
      } else {
        switch ($event.pointerType) {
          // Set range max to min + RANGE_STEP
          case PointerType.Min:
            currentSlider.max = Utils.round(currentSlider.max + RANGE_STEP, RANGE_DECIMAL);
            newNextValue = currentSlider.max;
            adjustNext = true;
            break;
          // Set range min to max - RANGE_STEP
          case PointerType.Max:
            currentSlider.min = Utils.round(currentSlider.min - RANGE_STEP, RANGE_DECIMAL);
            newPrevValue = currentSlider.min;
            adjustPrev = true;
            break;
          default:
            break;
        }
      }
    }

    if (adjustNext) {
      nextSlider.min = Utils.round(newNextValue, RANGE_DECIMAL);

      // Set next slider range min value to current range max
      if (nextSlider.max !== null && nextSlider.min >= nextSlider.max) {
        nextSlider.max = Utils.round(nextSlider.min + RANGE_STEP, RANGE_DECIMAL);
        // Call recursively to ensure following sliders are also set correctly
        this.adjustSiblingSlider(
          { pointerType: PointerType.Max, highValue: nextSlider.max, value: nextSlider.min },
          type,
          index + 1
        );
      }
    }

    if (adjustPrev) {
      prevSlider.max = Utils.round(newPrevValue, RANGE_DECIMAL);

      // Set previous slider range max value to current range min
      if (prevSlider.min !== null && prevSlider.min >= prevSlider.max) {
        prevSlider.min = Utils.round(prevSlider.max - RANGE_STEP, RANGE_DECIMAL);
        // Call recursively to ensure preceding sliders are also set correctly
        this.adjustSiblingSlider(
          { pointerType: PointerType.Min, highValue: prevSlider.max, value: prevSlider.min },
          type,
          index - 1
        );
      }
    }

    this.checkChanges();
  }

  calcMinLimit(i) {
    if (i === 0) {
      return this.minLimit;
    }

    return this.minLimit + (i - 1) * RANGE_STEP;
  }

  calcMaxLimit(type, scale, i) {
    if (i === scale - 1) {
      return this.maxLimit;
    }

    return this.maxLimit - (scale - 2 - i) * RANGE_STEP;
  }

  resetSlider(type: string) {
    for (let i = 0; i < this.sentimentRanges[type][this.sentimentScale].length; i++) {
      this.sentimentRanges[type][this.sentimentScale][i].min =
        this.defaultSentimentRanges[type][this.sentimentScale][i].min;
      this.sentimentRanges[type][this.sentimentScale][i].max =
        this.defaultSentimentRanges[type][this.sentimentScale][i].max;
    }

    this.checkChanges();
  }

  checkChanges() {
    this.hasChanges = !Utils.deepEquals(this.sentimentRanges, this.project.polarities);
  }

  translate(scale: number, index: number) {
    if (scale === 3) {
      return (value: number, type: LabelType) => {
        if (index === 0) {
          return `≦${value}`;
        } else if (index % 2) {
          if (type === LabelType.Low) {
            return `>${value}`;
          }
          return `<${value}`;
        } else {
          if (type === LabelType.Low) {
            return `≧${value}`;
          }
          return `≦${value}`;
        }
      };
    } else {
      return (value: number, type: LabelType) => {
        if (index === 0) {
          return `≦${value}`;
        } else if (index === 1) {
          if (type === LabelType.Low) {
            return `>${value}`;
          }
          return `≦${value}`;
        } else if (index === 2) {
          if (type === LabelType.Low) {
            return `>${value}`;
          }
          return `<${value}`;
        } else if (index === 3) {
          if (type === LabelType.Low) {
            return `≧${value}`;
          }
          return `<${value}`;
        } else if (index === 4) {
          return `≧${value}`;
        }
      };
    }
  }

  combineLabels(minLabel, maxLabel) {
    return `${minLabel} ${maxLabel}`;
  }

  getSliderOptions(type, scale, i) {
    if (!this.sliderOptions[type][scale][i]) {
      this.sliderOptions[type][scale][i] = {
        floor: -1,
        ceil: 1,
        step: this.step,
        tickStep: this.step,

        minLimit: this.calcMinLimit(i),
        maxLimit: this.calcMaxLimit(type, scale, i),
        translate: this.translateFns[scale][i],
        combineLabels: this.combineLabels,
        animate: false,
        noSwitching: true,
        hideLimitLabels: true,
        showSelectionBar: i === 0,
        showSelectionBarEnd: i === scale - 1,
        disabled: this.readOnly,
      };
    }

    return this.sliderOptions[type][scale][i];
  }

  cancel() {}

  save() {
    const transformData = (data, type: string, scale: number) => {
      data[type][scale] = this.sentimentRanges[type][scale].map((sentiment) => ({
        ...sentiment,
      }));

      data[type][scale][0].min = null;
      data[type][scale][scale - 1].max = null;
    };

    const result = {
      document: {
        3: [],
        5: [],
      },
      feature: {
        3: [],
        5: [],
      },
    };

    this.scaleTypes.forEach((type) => {
      this.scales.forEach((scale) => {
        transformData(result, type.id, scale);
      });
    });
    this.hasChanges = false;
    return result;
  }
}
